import Loader from '@/components/Loader';
import useAssetNews from '@/hooks/useAssetNews';
import { NewsItem } from '@/lib/News/types';
import Analytics from '@/system/Analytics';
import { IndividualAssetEvents } from '@/system/Analytics/events/IndividualAssetEvents';
import ArrayUtils from '@/utils/ArrayUtils';
import clsx from 'clsx';
import dayjs from 'dayjs';
import Link from 'next/link';
import { twMerge } from 'tailwind-merge';

interface INewsProps {
  className?: string;
  symbol: string;
}

function News({ className, symbol }: INewsProps) {
  const { data: news, isLoading } = useAssetNews(symbol, 4);

  const renderNewsItem = (item: NewsItem) => {
    return (
      <Link
        key={item.snippet}
        href={item.url || '#'}
        target="_blank"
        rel="noopener noreferrer"
        className="flex w-full justify-around space-x-4 py-4 h-[144px] text-left rounded-2xl md:hover:bg-translucent px-4"
        onClick={() => Analytics.track(IndividualAssetEvents.CLICKED_NEWS_ITEM, item)}
      >
        <div>
          <h6 className="text-secondary">
            {item.sourceName} · {dayjs(item.date).fromNow(true)}
          </h6>
          <h4 className=" line-clamp">{item.snippet}</h4>
        </div>
        <img
          src={item.imageSource}
          alt={item.sourceName}
          className="rounded-2xl w-16 h-16 object-cover object-center"
        />
      </Link>
    );
  };

  if (!isLoading && !news?.length) {
    return null;
  }

  return (
    <div
      className={twMerge(
        clsx(
          'bg-darkglass p-4 rounded-2xl flex flex-col lg:flex-row divide-y-0.5 lg:divide-x-0.5 divide-line-primary lg:divide-y-0 lg:space-x-4',
          className
        )
      )}
    >
      {!isLoading ? (
        <>
          <div
            className={twMerge(
              clsx('flex flex-col md:flex-row w-full', {
                'md:flex-col divide-y-0.5 divide-line-primary': ArrayUtils.orEmptyArray(news).length > 2,
              })
            )}
          >
            {ArrayUtils.orEmptyArray(news).slice(0, 2).map(renderNewsItem)}
          </div>
          {ArrayUtils.orEmptyArray(news).length > 2 && (
            <div className="flex flex-col w-full divide-y-0.5 divide-line-primary">
              {ArrayUtils.orEmptyArray(news).slice(2, 4).map(renderNewsItem)}
            </div>
          )}
        </>
      ) : (
        <Loader className="mx-auto" />
      )}
    </div>
  );
}

export default News;
